import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { flexRender, getCoreRowModel, RowSelectionState, useReactTable } from '@tanstack/react-table';
import { Loader2, PlusCircle, UserMinus, Users2, Info } from 'lucide-react';
import { StatCard } from '@/components/ui/StatCard';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { AuditService } from '@/services/AuditService';
import { useUsuarios, AppUser } from '@/hooks/useUsuarios';
import { createAdminUserColumns } from './components/columnsUsuariosAdmin';
import { UsuariosAdminToolbar } from '@/components/administracao/UsuariosAdminToolbar';
import { UserEditAdminModal } from '@/components/administracao/UserEditAdminModal';
import UserForm from '@/components/UserForm';
import { FixedPagination } from '@/components/ui/fixed-pagination';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getRoleLabel, getUserDisplayName } from '@/lib/admin-users';

const UsuariosListaSkeleton = () => (
  <div className="space-y-6 px-6 py-6 bg-background">
    <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 mb-8">
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className="h-32 rounded-xl bg-card border border-border animate-pulse" />
      ))}
    </div>
    <div className="space-y-4">
      <Skeleton className="h-14 w-full rounded-xl" />
      <Skeleton className="h-[500px] w-full rounded-xl" />
    </div>
  </div>
);


const getStatusLabel = (status: AppUser['status']) => {
  const labels: Record<AppUser['status'], string> = {
    APPROVED: 'aprovado',
    PENDING: 'pendente',
    REJECTED: 'rejeitado',
    INACTIVE: 'inativo',
  };

  return labels[status];
};

const buildProfilePayloadFromUser = (user: AppUser, nextStatus: AppUser['status']) => ({
  id: user.id,
  email: user.email,
  name: user.profile_name || getUserDisplayName(user),
  avatar_url: user.avatar_url || user.user_metadata?.avatar_url || null,
  role: user.role || 'HOMOLOGACAO',
  status: nextStatus,
});

const UsuariosAdminPage = () => {
  const { toast } = useToast();
  const { data: usuarios, isLoading, isError, error } = useUsuarios();
  const { signUp, user: currentUser } = useAuth();
  const queryClient = useQueryClient();

  const [modalOpen, setModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AppUser | null>(null);
  const [globalSearch, setGlobalSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | AppUser['status']>('ALL');
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [bulkActionLoading, setBulkActionLoading] = useState<AppUser['status'] | null>(null);

  const handleEditUser = (user: AppUser) => {
    setSelectedUser(user);
    setEditModalOpen(true);
  };

  const columns = createAdminUserColumns({
    onEdit: handleEditUser,
    onChangeStatus: async (user, nextStatus) => {
      await handleUpdateUserStatus(user, nextStatus);
    },
    onResetPassword: async (user) => {
      await handleSendResetPassword(user);
    },
  });

  useEffect(() => {
    if (columns.length > 0 && visibleColumns.length === 0) {
      setVisibleColumns(columns.map((column) => (column.id || (column as any).accessorKey) as string).filter(Boolean));
    }
  }, [columns, visibleColumns.length]);

  const countsByStatus = useMemo(
    () =>
      (usuarios || []).reduce(
        (accumulator, user) => {
          accumulator[user.status] += 1;
          return accumulator;
        },
        {
          APPROVED: 0,
          PENDING: 0,
          REJECTED: 0,
          INACTIVE: 0,
        } as Record<AppUser['status'], number>
      ),
    [usuarios]
  );

  const filteredUsuarios = useMemo(() => {
    const search = globalSearch.trim().toLowerCase();

    return (usuarios || []).filter((user) => {
      const matchesStatus = statusFilter === 'ALL' ? true : user.status === statusFilter;
      if (!matchesStatus) return false;
      if (!search) return true;

      return [
        user.email,
        getUserDisplayName(user),
        user.role,
        getRoleLabel(user.role),
        user.status,
        getStatusLabel(user.status),
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(search));
    });
  }, [globalSearch, statusFilter, usuarios]);

  useEffect(() => {
    setCurrentPage(1);
    setRowSelection({});
  }, [globalSearch, statusFilter, pageSize]);

  const totalItems = filteredUsuarios.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  const paginatedUsuarios = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredUsuarios.slice(startIndex, startIndex + pageSize);
  }, [currentPage, filteredUsuarios, pageSize]);

  const selectedUsers = useMemo(
    () => paginatedUsuarios.filter((_, index) => Boolean(rowSelection[index])),
    [paginatedUsuarios, rowSelection]
  );

  const table = useReactTable({
    data: paginatedUsuarios,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      rowSelection,
      columnVisibility: Object.fromEntries(
        columns.map((column) => {
          const columnId = (column.id || (column as any).accessorKey) as string;
          return [columnId, visibleColumns.includes(columnId)];
        })
      ),
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
  });

  const invalidateUsers = async () => {
    await queryClient.invalidateQueries({ queryKey: ['usuarios'] });
    await table.resetRowSelection();
    setRowSelection({});
  };

  const upsertUserProfileStatus = async (user: AppUser, nextStatus: AppUser['status']) => {
    const payload = buildProfilePayloadFromUser(user, nextStatus);
    const { error: saveError } = await supabase.from('profiles').upsert(payload, { onConflict: 'id' });
    if (saveError) throw saveError;
  };

  async function handleUpdateUserStatus(user: AppUser, nextStatus: AppUser['status']) {
    try {
      await upsertUserProfileStatus(user, nextStatus);
      await AuditService.logActivity(
        'admin_update_user_status',
        {
          user_id: user.id,
          email: user.email,
          previous_status: user.status,
          next_status: nextStatus,
        },
        currentUser?.id
      );

      toast({
        title: 'Status atualizado',
        description: `${getUserDisplayName(user)} agora está como ${getStatusLabel(nextStatus)}.`,
        variant: 'success',
      });

      await invalidateUsers();
    } catch (actionError: any) {
      toast({
        title: 'Erro ao atualizar status',
        description: actionError?.message || 'Não foi possível atualizar o usuário.',
        variant: 'destructive',
      });
    }
  }

  async function handleBulkStatusChange(nextStatus: AppUser['status']) {
    if (!selectedUsers.length) return;

    setBulkActionLoading(nextStatus);

    try {
      await Promise.all(selectedUsers.map((user) => upsertUserProfileStatus(user, nextStatus)));

      await AuditService.logActivity(
        'admin_bulk_update_user_status',
        {
          user_ids: selectedUsers.map((user) => user.id),
          next_status: nextStatus,
          count: selectedUsers.length,
        },
        currentUser?.id
      );

      toast({
        title: 'Ação em lote concluída',
        description: `${selectedUsers.length} usuários foram marcados como ${getStatusLabel(nextStatus)}.`,
        variant: 'success',
      });

      await invalidateUsers();
    } catch (actionError: any) {
      toast({
        title: 'Erro na ação em lote',
        description: actionError?.message || 'Não foi possível concluir a atualização em lote.',
        variant: 'destructive',
      });
    } finally {
      setBulkActionLoading(null);
    }
  }

  async function handleSendResetPassword(user: AppUser) {
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(user.email || '', {
        redirectTo: `${window.location.origin}/#/reset-password-confirm`,
      });

      if (resetError) throw resetError;

      await AuditService.logActivity(
        'admin_send_password_reset',
        { user_id: user.id, email: user.email },
        currentUser?.id
      );

      toast({
        title: 'E-mail enviado',
        description: `O link de redefinição foi enviado para ${user.email}.`,
        variant: 'success',
      });
    } catch (actionError: any) {
      toast({
        title: 'Erro ao enviar redefinição',
        description: actionError?.message || 'Não foi possível enviar o e-mail.',
        variant: 'destructive',
      });
    }
  }

  const handleUserFormSubmit = async (data: any) => {
    try {
      setIsSubmitting(true);
      const signUpResult = await signUp(data.email, data.password, data.full_name, data.role);
      
      const userId = signUpResult?.user?.id;
      let avatarPath = '';

      // Upload do Avatar se houver arquivo
      if (userId && data.avatarFile) {
        const file = data.avatarFile;
        const fileExt = file.name.split('.').pop();
        const fileName = `${userId}-${Math.random()}.${fileExt}`;
        const filePath = `avatars/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('imgsinsecrj')
          .upload(filePath, file);

        if (!uploadError) {
          avatarPath = filePath;
          // Atualizar o perfil com a URL do avatar
          await supabase
            .from('profiles')
            .update({ avatar_url: filePath })
            .eq('id', userId);
        }
      }

      await AuditService.logActivity(
        'CREATE_USER',
        { 
          email: data.email, 
          role: data.role, 
          name: data.full_name,
          has_avatar: !!avatarPath 
        },
        currentUser?.id || 'system'
      );

      toast({
        title: 'Usuário criado com sucesso!',
        description: 'Um novo usuário foi cadastrado no sistema.',
      });
      setModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
    } catch (error: any) {
      toast({
        title: 'Erro ao criar usuário',
        description: error.message || 'Ocorreu um erro inesperado.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleColumn = (columnId: string) => {
    setVisibleColumns((current) =>
      current.includes(columnId)
        ? current.filter((column) => column !== columnId)
        : [...current, columnId]
    );
  };

  if (isLoading) {
    return <UsuariosListaSkeleton />;
  }

  if (isError) {
    const isSecurityError = error?.message.includes('permission denied');
    return (
      <div className="mx-6 mt-6 rounded-3xl border border-destructive/20 bg-destructive/10 p-5 text-destructive">
        <h3 className="font-semibold">Erro ao carregar usuários</h3>
        <p className="mt-2 text-sm">
          {isSecurityError ? 'Você não tem permissão para consultar os usuários.' : error.message}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-32">
      <div className="mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6 lg:px-8">
        <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 mb-4">
        <StatCard
          title="Base monitorada"
          value={usuarios?.length || 0}
          subtitle="Todos os usuários registrados"
          trend="neutral"
          trendLabel="Geral"
        />
        <StatCard
          title="Aprovados"
          value={countsByStatus.APPROVED}
          subtitle="Contas com acesso liberado"
          trend="positive"
          trendLabel="Ativos"
        />
        <StatCard
          title="Pendentes"
          value={countsByStatus.PENDING}
          subtitle="Aguardando decisão administrativa"
          trend="neutral"
          trendLabel="Aguardando"
        />
        <StatCard
          title="Bloqueados"
          value={countsByStatus.REJECTED + countsByStatus.INACTIVE}
          subtitle="Rejeitados ou inativados"
          trend="negative"
          trendLabel="Restritos"
        />
      </div>

      <UsuariosAdminToolbar
        searchValue={globalSearch}
        onSearchChange={setGlobalSearch}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        columns={columns}
        visibleColumns={visibleColumns}
        onColumnToggle={toggleColumn}
        totalUsuarios={usuarios?.length || 0}
        filteredUsuarios={filteredUsuarios.length}
        countsByStatus={countsByStatus}
        rightAction={
          <Button variant="success" className="h-11 rounded-xl px-5 shadow-lg shadow-emerald-500/10" onClick={() => setModalOpen(true)}>
            <PlusCircle className="mr-2 h-4 w-4" />
            Novo usuário
          </Button>
        }
      />

      {selectedUsers.length > 0 && (
        <div className="flex flex-col gap-3 rounded-2xl border border-sky-500/20 bg-sky-500/5 p-4 lg:flex-row lg:items-center lg:justify-between animate-in fade-in slide-in-from-top-2 duration-300">
          <div className="flex items-center gap-3 text-sm text-sky-600 dark:text-sky-400">
            <div className="p-1.5 bg-sky-500/10 rounded-lg">
              <Info className="h-4 w-4" />
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="border-sky-500/30 bg-background text-sky-600 dark:text-sky-400 font-bold">
                {selectedUsers.length} selecionado(s)
              </Badge>
              <span className="font-medium">Use as ações em lote para agilizar a administração.</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="success" className="rounded-lg px-4" onClick={() => handleBulkStatusChange('APPROVED')} disabled={!!bulkActionLoading}>
              {bulkActionLoading === 'APPROVED' ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Aprovar lote'}
            </Button>
            <Button size="sm" variant="outline" className="rounded-lg px-4" onClick={() => handleBulkStatusChange('PENDING')} disabled={!!bulkActionLoading}>
              {bulkActionLoading === 'PENDING' ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Voltar para pendente'}
            </Button>
            <Button size="sm" variant="outline" className="rounded-lg px-4 hover:text-destructive" onClick={() => handleBulkStatusChange('INACTIVE')} disabled={!!bulkActionLoading}>
              {bulkActionLoading === 'INACTIVE' ? <Loader2 className="h-3 w-3 animate-spin" /> : (
                <>
                  <UserMinus className="mr-1 h-3.5 w-3.5" />
                  Inativar lote
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-sm">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-muted/50">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="border-border hover:bg-transparent">
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id} className="h-12 text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold">
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} className="border-border transition-colors hover:bg-muted/30">
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className="py-4">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={table.getAllColumns().length} className="h-40 text-center">
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <Users2 className="h-8 w-8 opacity-20" />
                      <div className="text-base font-medium">Nenhum usuário encontrado</div>
                      <div className="text-sm opacity-60">Ajuste a busca ou os filtros para continuar.</div>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

        <FixedPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          onPageSizeChange={(newPageSize) => {
            setPageSize(newPageSize);
            setCurrentPage(1);
          }}
          pageSize={pageSize}
          totalItems={totalItems}
        />
      </div>

      <Dialog open={modalOpen} onOpenChange={(nextOpen) => !nextOpen && setModalOpen(false)}>
        <DialogContent className="flex flex-col gap-0 overflow-hidden p-0 max-w-2xl max-h-[90vh] rounded-3xl border-0 shadow-2xl bg-card">
          <DialogHeader className="border-b bg-muted/30 px-6 py-4 text-left border-border">
            <DialogTitle className="text-lg font-bold text-foreground">Novo usuário</DialogTitle>
            <p className="text-sm text-muted-foreground font-medium">
              Cadastre um novo usuário preenchendo as informações de acesso abaixo.
            </p>
          </DialogHeader>
          
          <div className="overflow-y-auto flex-1 p-6">
            <UserForm
              onSubmit={handleUserFormSubmit}
              isLoading={isSubmitting}
              formId="novo-usuario-form"
              hideSubmit={true}
            />
          </div>

          <div className="flex justify-end gap-3 border-t bg-muted/20 px-6 py-4 border-border">
            <Button 
              variant="outline" 
              onClick={() => setModalOpen(false)} 
              disabled={isSubmitting}
              className="rounded-xl h-11 px-6 border-input"
            >
              Cancelar
            </Button>
            <Button 
              type="submit" 
              form="novo-usuario-form" 
              variant="success" 
              disabled={isSubmitting}
              className="rounded-xl h-11 px-8 shadow-lg shadow-emerald-500/10"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Criando conta...
                </>
              ) : (
                'Criar conta'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      <UserEditAdminModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        user={selectedUser}
        onSaved={invalidateUsers}
      />
    </div>
  );
};

export default UsuariosAdminPage;
